<template>
  <div class="container pull-up">
    <div class="row">
      <div class="col-md-12">
        <div class="card m-b-30">
          <div class="card-header">
            <el-row>
              <el-col :span="12">
                <h5 class="m-b-0">
                  <i class="mdi mdi-account-group"></i> {{ $t('role.user_role.manager.title') }}
                </h5>
              </el-col>
              <el-col :span="12" class="text-right">
                <el-button type="primary" @click="showBulkAssignModal = true">
                  <i class="el-icon-plus"></i> {{ $t('role.user_role.button.bulk_assign') }}
                </el-button>
              </el-col>
            </el-row>
          </div>
          <div class="card-body">
            <!-- Filters -->
            <div class="row mb-3">
              <div class="col-md-4">
                <el-input
                  v-model="searchQuery"
                  placeholder="Search users..."
                  prefix-icon="el-icon-search"
                  clearable
                />
              </div>
              <div class="col-md-4">
                <el-select
                  v-model="selectedRole"
                  placeholder="Filter by role"
                  clearable
                  @change="filterUsers"
                >
                  <el-option
                    v-for="role in roles"
                    :key="role.id"
                    :label="role.name"
                    :value="role.id"
                  />
                </el-select>
              </div>
              <div class="col-md-4">
                <el-select
                  v-model="statusFilter"
                  placeholder="Filter by status"
                  clearable
                  @change="filterUsers"
                >
                  <el-option label="Active" value="active" />
                  <el-option label="Inactive" value="inactive" />
                </el-select>
              </div>
            </div>

            <!-- Users Table -->
            <el-table
              :data="paginatedUsers"
              style="width: 100%"
              v-loading="loading"
            >
              <el-table-column prop="name" label="User Name" width="200">
                <template slot-scope="scope">
                  <div class="user-info">
                    <div class="user-name">{{ scope.row.name }}</div>
                    <div class="user-email text-muted">{{ scope.row.email }}</div>
                  </div>
                </template>
              </el-table-column>

              <el-table-column prop="roles" label="Current Roles" min-width="300">
                <template slot-scope="scope">
                  <div class="user-roles">
                    <el-tag
                      v-for="role in scope.row.roles"
                      :key="role.id"
                      :type="getRoleTagType(role.name)"
                      size="small"
                      class="role-tag"
                    >
                      {{ role.name }}
                    </el-tag>
                    <span v-if="!scope.row.roles || scope.row.roles.length === 0" class="text-muted">
                      No roles assigned
                    </span>
                  </div>
                </template>
              </el-table-column>

              <el-table-column prop="active" label="Status" width="100">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.active ? 'success' : 'danger'" size="small">
                    {{ scope.row.active ? 'Active' : 'Inactive' }}
                  </el-tag>
                </template>
              </el-table-column>

              <el-table-column label="Actions" width="200">
                <template slot-scope="scope">
                  <el-button-group>
                    <el-button
                      size="mini"
                      type="primary"
                      @click="assignRoles(scope.row)"
                    >
                      <i class="el-icon-edit"></i> Manage Roles
                    </el-button>
                    <el-button
                      size="mini"
                      type="info"
                      @click="viewUserPermissions(scope.row)"
                    >
                      <i class="el-icon-view"></i> Permissions
                    </el-button>
                  </el-button-group>
                </template>
              </el-table-column>
            </el-table>

            <!-- Pagination -->
            <div class="text-center mt-3">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="pagination.current_page"
                :page-sizes="[10, 20, 50, 100]"
                :page-size="pagination.per_page"
                layout="total, sizes, prev, pager, next, jumper"
                :total="pagination.total"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- User Role Assignment Modal -->
    <el-dialog
      title="Assign Roles to User"
      :visible.sync="showAssignModal"
      width="60%"
      :close-on-click-modal="false"
    >
      <div v-if="selectedUser">
        <h6>User: {{ selectedUser.name }}</h6>
        <el-transfer
          v-model="assignedRoles"
          :data="availableRoles"
          :titles="['Available Roles', 'Assigned Roles']"
          filterable
        />
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showAssignModal = false">Cancel</el-button>
        <el-button type="primary" @click="saveUserRoles" :loading="saving">
          Save Changes
        </el-button>
      </span>
    </el-dialog>

    <!-- User Permissions Modal -->
    <el-dialog
      title="User Permissions"
      :visible.sync="showPermissionsModal"
      width="70%"
    >
      <div v-if="selectedUser">
        <h6>User: {{ selectedUser.name }}</h6>
        <div class="permissions-list">
          <el-tag
            v-for="permission in userPermissions"
            :key="permission.id"
            type="info"
            size="small"
            class="permission-tag"
          >
            {{ permission.name }}
          </el-tag>
          <span v-if="!userPermissions || userPermissions.length === 0" class="text-muted">
            No permissions assigned
          </span>
        </div>
      </div>
    </el-dialog>

    <!-- Bulk Role Assignment Modal -->
    <el-dialog
      title="Bulk Role Assignment"
      :visible.sync="showBulkAssignModal"
      width="50%"
    >
      <el-form :model="bulkAssignForm" label-width="120px">
        <el-form-item label="Select Users">
          <el-select
            v-model="bulkAssignForm.userIds"
            multiple
            placeholder="Select users"
            style="width: 100%"
          >
            <el-option
              v-for="user in users"
              :key="user.id"
              :label="user.name"
              :value="user.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Select Roles">
          <el-select
            v-model="bulkAssignForm.roleIds"
            multiple
            placeholder="Select roles"
            style="width: 100%"
          >
            <el-option
              v-for="role in roles"
              :key="role.id"
              :label="role.name"
              :value="role.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Action">
          <el-radio-group v-model="bulkAssignForm.action">
            <el-radio label="assign">Assign Roles</el-radio>
            <el-radio label="remove">Remove Roles</el-radio>
            <el-radio label="replace">Replace All Roles</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showBulkAssignModal = false">Cancel</el-button>
        <el-button type="primary" @click="executeBulkAssign" :loading="bulkProcessing">
          Execute
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'UserRoleManager',
  data () {
    return {
      users: [],
      roles: [],
      filteredUsers: [],
      searchQuery: '',
      selectedRole: null,
      statusFilter: null,
      loading: false,
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0
      },
      showAssignModal: false,
      showPermissionsModal: false,
      showBulkAssignModal: false,
      selectedUser: null,
      assignedRoles: [],
      availableRoles: [],
      userPermissions: [],
      saving: false,
      bulkProcessing: false,
      bulkAssignForm: {
        userIds: [],
        roleIds: [],
        action: 'assign'
      }
    }
  },
  methods: {
    withLoading (fn, loadingKey = 'loading') {
      this[loadingKey] = true
      return fn().finally(() => {
        this[loadingKey] = false
      })
    },
    showSuccess (message) {
      this.$message.success(message)
    },
    showError (message) {
      this.$message.error(message)
    },
    async loadData () {
      try {
        await this.withLoading(async () => {
          const [usersData, rolesData] = await Promise.all([
            this.$userService.getUsers(),
            this.$roleService.getRoles()
          ])

          this.users = usersData.map(user => ({
            ...user,
            roles: user.roles || []
          }))
          this.roles = rolesData
          this.filteredUsers = [...this.users]
          this.pagination.total = this.users.length
        }, 'loading')
      } catch (error) {
        console.error('Error loading data:', error)
        this.showError('Failed to load data. Please try again.')
      }
    },
    filterUsers () {
      let filtered = [...this.users]

      // Search filter
      if (this.searchQuery) {
        filtered = filtered.filter(user =>
          user.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(this.searchQuery.toLowerCase())
        )
      }

      // Role filter
      if (this.selectedRole) {
        filtered = filtered.filter(user =>
          user.roles && user.roles.some(role => role.id === this.selectedRole)
        )
      }

      // Status filter
      if (this.statusFilter) {
        const isActive = this.statusFilter === 'active'
        filtered = filtered.filter(user => user.active === isActive)
      }

      this.filteredUsers = filtered
      this.pagination.total = filtered.length
      // Reset to first page when filtering
      this.pagination.current_page = 1
    },
    getRoleTagType (roleName) {
      const types = {
        administrator: 'danger',
        manager: 'warning',
        cashier: 'success',
        service: 'info',
        employee: 'default'
      }
      return types[roleName] || 'default'
    },
    async assignRoles (user) {
      this.selectedUser = { ...user } // Create a copy to avoid mutations
      // Initialize with current user roles (handle case where user has no roles)
      this.assignedRoles = (user.roles || []).map(role => role.id).filter(id => id != null)

      // Load available roles for transfer component
      this.availableRoles = this.roles.map(role => ({
        key: role.id,
        label: role.name,
        guard_name: role.guard_name || 'api'
      }))

      this.showAssignModal = true
    },
    async saveUserRoles () {
      this.saving = true
      try {
        const response = await this.$roleService.assignUserRoles(this.selectedUser.id, {
          roles: this.assignedRoles
        })

        // Reload user data from server to get fresh roles
        const updatedUsers = await this.$userService.getUsers()
        const updatedUser = updatedUsers.find(u => u.id === this.selectedUser.id)

        // Update local data with fresh data from server
        const userIndex = this.users.findIndex(u => u.id === this.selectedUser.id)
        if (userIndex !== -1 && updatedUser) {
          this.users[userIndex] = {
            ...this.users[userIndex],
            roles: updatedUser.roles || []
          }
          // Also update selectedUser if modal is still referencing it
          this.selectedUser.roles = updatedUser.roles || []
        }

        this.showSuccess(response?.message || 'User roles updated successfully')
        this.showAssignModal = false
        this.filterUsers() // Refresh filtered list
      } catch (error) {
        console.error('Error updating user roles:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Failed to update user roles. Please try again.'
        this.showError(errorMessage)
      } finally {
        this.saving = false
      }
    },
    async viewUserPermissions (user) {
      this.selectedUser = user

      try {
        // Fetch user roles with permissions from the API
        const userRoles = await this.$roleService.getUserRoles(user.id)

        // Fetch permissions for each role
        const permissionPromises = userRoles.map(role =>
          this.$roleService.getRolePermissions(role.id).catch(() => [])
        )
        const rolePermissionsArrays = await Promise.all(permissionPromises)

        // Flatten and deduplicate permissions
        const permissionsMap = new Map()
        rolePermissionsArrays.forEach(permissions => {
          permissions.forEach(permission => {
            if (!permissionsMap.has(permission.id)) {
              permissionsMap.set(permission.id, permission)
            }
          })
        })

        this.userPermissions = Array.from(permissionsMap.values())
      } catch (error) {
        console.error('Error loading user permissions:', error)
        this.userPermissions = []
        this.showError('Failed to load user permissions')
      }

      this.showPermissionsModal = true
    },
    async executeBulkAssign () {
      if (!this.bulkAssignForm.userIds.length || !this.bulkAssignForm.roleIds.length) {
        this.$message.warning('Please select users and roles')
        return
      }

      this.bulkProcessing = true
      try {
        const promises = this.bulkAssignForm.userIds.map(userId => {
          const user = this.users.find(u => u.id === userId)
          if (!user) {
            return Promise.reject(new Error(`User with ID ${userId} not found`))
          }

          let roleIds = []

          switch (this.bulkAssignForm.action) {
            case 'assign': {
              // Add roles to existing ones
              const existingRoleIds = (user.roles || []).map(r => r.id)
              roleIds = [...new Set([...existingRoleIds, ...this.bulkAssignForm.roleIds])]
              break
            }
            case 'remove': {
              // Remove selected roles
              roleIds = (user.roles || [])
                .filter(r => !this.bulkAssignForm.roleIds.includes(r.id))
                .map(r => r.id)
              break
            }
            case 'replace': {
              // Replace all roles
              roleIds = this.bulkAssignForm.roleIds
              break
            }
          }

          return this.$roleService.assignUserRoles(userId, { roles: roleIds })
        })

        await Promise.all(promises)
        this.showSuccess('Bulk role assignment completed successfully')
        this.showBulkAssignModal = false
        this.bulkAssignForm = { userIds: [], roleIds: [], action: 'assign' }
        await this.loadData() // Reload data to get fresh roles
      } catch (error) {
        console.error('Error in bulk assign:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Failed to execute bulk role assignment. Please try again.'
        this.showError(errorMessage)
      } finally {
        this.bulkProcessing = false
      }
    },
    handleSizeChange (val) {
      this.pagination.per_page = val
      this.pagination.current_page = 1
      this.filterUsers() // Just re-filter, don't reload from server
    },
    handleCurrentChange (val) {
      this.pagination.current_page = val
      this.filterUsers() // Just re-filter, don't reload from server
    }
  },
  computed: {
    paginatedUsers () {
      const start = (this.pagination.current_page - 1) * this.pagination.per_page
      const end = start + this.pagination.per_page
      return this.filteredUsers.slice(start, end)
    }
  },
  watch: {
    searchQuery () {
      this.filterUsers()
    },
    selectedRole () {
      this.filterUsers()
    },
    statusFilter () {
      this.filterUsers()
    }
  },
  async mounted () {
    this.$emit('parent-title', this.$t('role.user_role.manager.title'))
    await this.loadData()
  }
}
</script>

<style scoped>
.user-info {
  line-height: 1.4;
}

.user-name {
  font-weight: 500;
  color: #333;
}

.user-email {
  font-size: 12px;
}

.user-roles {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.role-tag {
  margin: 2px;
}

.permissions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  max-height: 300px;
  overflow-y: auto;
}

.permission-tag {
  margin: 2px;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}
</style>
